/**
 * OmniLeagues - Public Booking Page (no auth required).
 * Route: /book/:orgId/:eventSlug?token=...
 * Calendly-style: month calendar → slot picker → request form → confirmation.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  Clock,
  CheckCircle2,
  Loader2,
  MapPin,
  Home as HomeIcon,
  Plane,
} from 'lucide-react';
import {
  publicEventType,
  publicSlots,
  publicSubmit,
  formatInTz,
  formatTimeOnly,
  dateKeyInTz,
} from '../../components/booking/bookingApi';

export default function PublicBookingPage() {
  const { orgId, eventSlug } = useParams();
  const [params] = useSearchParams();
  const token = params.get('token');

  const [loading, setLoading] = useState(true);
  const [eventType, setEventType] = useState(null);
  const [org, setOrg] = useState(null);
  const [monthRef, setMonthRef] = useState(() => new Date());
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    team_name: '',
    sport: '',
    side: 'away',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  // Fetch event type details
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await publicEventType(orgId, eventSlug, token);
        setEventType(res.event_type);
        setOrg(res.org);
        setForm((f) => ({ ...f, side: res.event_type?.default_home_away === 'home' ? 'away' : 'home' }));
      } catch {
        setEventType(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [orgId, eventSlug, token]);

  // Fetch slots for visible month
  useEffect(() => {
    if (!eventType) return;
    (async () => {
      setLoadingSlots(true);
      try {
        const y = monthRef.getFullYear();
        const m = monthRef.getMonth();
        const fromDate = `${y}-${String(m + 1).padStart(2, '0')}-01`;
        const lastDay = new Date(y, m + 1, 0).getDate();
        const toDate = `${y}-${String(m + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        const res = await publicSlots(orgId, eventSlug, fromDate, toDate);
        setSlots(res.slots || []);
      } catch {
        setSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    })();
  }, [eventType, monthRef, orgId, eventSlug]);

  const tz = eventType?.timezone || 'America/New_York';

  const slotsByDate = useMemo(() => {
    const map = {};
    slots.forEach((s) => {
      const k = dateKeyInTz(s.start_utc, tz);
      if (!map[k]) map[k] = [];
      map[k].push(s);
    });
    return map;
  }, [slots, tz]);

  const monthGrid = useMemo(() => {
    const y = monthRef.getFullYear();
    const m = monthRef.getMonth();
    const first = new Date(y, m, 1);
    const pad = first.getDay();
    const last = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < pad; i++) cells.push(null);
    for (let d = 1; d <= last; d++) {
      const date = new Date(y, m, d);
      cells.push(date);
    }
    return cells;
  }, [monthRef]);

  const keyOf = (d) =>
    d
      ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      : '';

  if (loading)
    return (
      <PageShell>
        <div className="flex items-center gap-2 justify-center py-24">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading…
        </div>
      </PageShell>
    );
  if (!eventType)
    return (
      <PageShell>
        <div className="text-center py-24">
          <p className="text-xl font-semibold">Booking page not found</p>
          <p className="text-slate-400 mt-2">This link may have been rotated or the event removed.</p>
        </div>
      </PageShell>
    );

  if (confirmation) {
    return (
      <PageShell>
        <Card className="bg-slate-800/60 border-slate-700">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Request submitted</h1>
            <p className="text-slate-300 mb-4">
              Your request is now with <strong>{org?.name || 'the scheduler'}</strong>. We'll email
              you as soon as it's reviewed.
            </p>
            <div className="inline-block text-left text-sm bg-slate-900/60 border border-slate-700 rounded-lg p-4">
              <div>
                <span className="text-slate-400">Event:</span> {eventType.name}
              </div>
              <div>
                <span className="text-slate-400">Time:</span>{' '}
                {formatInTz(confirmation.slot_start_utc, tz)}
              </div>
              <div>
                <span className="text-slate-400">Reference:</span> {confirmation.id.slice(0, 8)}
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-6">
              Save this link to cancel or check status:{' '}
              <a
                className="underline text-amber-400"
                href={`/manage/${confirmation.manage_token}`}
              >
                /manage/{confirmation.manage_token.slice(0, 10)}…
              </a>
            </p>
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  const handleSelectDate = (d) => {
    const k = keyOf(d);
    if (!slotsByDate[k]) return;
    setSelectedDate(k);
    setSelectedSlot(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSlot) return;
    if (!form.name.trim() || !form.email.trim() || !form.team_name.trim()) {
      toast.error('Please fill in name, email, and team name');
      return;
    }
    if (!form.sport.trim()) {
      toast.error('Please choose a sport / level');
      return;
    }
    setSubmitting(true);
    try {
      const res = await publicSubmit(orgId, eventSlug, {
        slot_start_utc: selectedSlot.start_utc,
        requester: {
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          organization: form.organization.trim(),
          team_name: form.team_name.trim(),
          sport: form.sport.trim(),
          side: form.side,
        },
        form_answers: form.notes ? { notes: form.notes } : {},
      });
      setConfirmation(res.data);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const monthLabel = monthRef.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <PageShell>
      <div className="grid md:grid-cols-2 gap-0 rounded-xl overflow-hidden border border-slate-700 bg-slate-800/50">
        {/* Left: summary */}
        <div className="p-6 border-r border-slate-700/50 bg-slate-900/40">
          <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">
            {org?.name || 'Schedule a game'}
          </div>
          <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
            <span
              className="w-3 h-3 rounded-full inline-block"
              style={{ backgroundColor: eventType.color }}
            />
            {eventType.name}
          </h1>
          {eventType.description && (
            <p className="text-slate-300 text-sm mb-4">{eventType.description}</p>
          )}
          <div className="space-y-2 text-sm text-slate-300">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-400" /> {eventType.duration_minutes} min
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-amber-400" /> {tz}
            </div>
            <div className="flex items-center gap-2">
              {eventType.default_home_away === 'home' ? (
                <HomeIcon className="h-4 w-4 text-amber-400" />
              ) : (
                <Plane className="h-4 w-4 text-amber-400" />
              )}{' '}
              Default: {eventType.default_home_away}
            </div>
          </div>
          <div className="mt-6 text-xs text-slate-500 border-t border-slate-700/50 pt-4">
            Nothing is confirmed until the scheduler approves your request. You'll get a confirmation
            email with next steps.
          </div>
        </div>

        {/* Right: picker / form */}
        <div className="p-6">
          {!selectedSlot ? (
            <>
              <div className="flex items-center justify-between mb-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setMonthRef(new Date(monthRef.getFullYear(), monthRef.getMonth() - 1, 1))
                  }
                  data-testid="public-month-prev"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="font-semibold">{monthLabel}</div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setMonthRef(new Date(monthRef.getFullYear(), monthRef.getMonth() + 1, 1))
                  }
                  data-testid="public-month-next"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid grid-cols-7 text-center text-[11px] text-slate-400 mb-1">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                  <div key={d}>{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1 mb-4">
                {monthGrid.map((d, i) => {
                  if (!d) return <div key={i} />;
                  const k = keyOf(d);
                  const has = !!slotsByDate[k];
                  const isSelected = selectedDate === k;
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => handleSelectDate(d)}
                      disabled={!has}
                      className={`aspect-square rounded-md text-sm flex items-center justify-center transition ${
                        isSelected
                          ? 'bg-amber-600 text-white'
                          : has
                          ? 'bg-slate-700/40 hover:bg-amber-600/30 text-white'
                          : 'text-slate-600 cursor-not-allowed'
                      }`}
                      data-testid={has ? `public-day-${k}` : undefined}
                    >
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>
              {loadingSlots && (
                <div className="text-sm text-slate-400 flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading slots…
                </div>
              )}
              {selectedDate && slotsByDate[selectedDate] && (
                <div>
                  <div className="text-sm font-semibold mb-2">
                    {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </div>
                  <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto pr-1">
                    {slotsByDate[selectedDate].map((s) => (
                      <Button
                        key={s.start_utc}
                        variant="outline"
                        className="justify-center"
                        onClick={() => setSelectedSlot(s)}
                        data-testid={`public-slot-${s.start_utc}`}
                      >
                        {formatTimeOnly(s.start_utc, tz)}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              {!loadingSlots && slots.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-8">
                  No open slots in this month. Try the next one.
                </p>
              )}
            </>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3" data-testid="public-booking-form">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-xs text-slate-400">Selected</div>
                  <div className="font-semibold">{formatInTz(selectedSlot.start_utc, tz)}</div>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => setSelectedSlot(null)}>
                  Change
                </Button>
              </div>
              <div>
                <Label>Your Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  data-testid="public-form-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                    data-testid="public-form-email"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Your Organization</Label>
                  <Input
                    value={form.organization}
                    onChange={(e) => setForm({ ...form, organization: e.target.value })}
                    placeholder="Cardinal Academy"
                  />
                </div>
                <div>
                  <Label>Your Team Name *</Label>
                  <Input
                    value={form.team_name}
                    onChange={(e) => setForm({ ...form, team_name: e.target.value })}
                    required
                    placeholder="Cardinal Varsity Boys BB"
                    data-testid="public-form-team"
                  />
                </div>
              </div>
              <div>
                <Label>Sport / Level *</Label>
                {(eventType.sport_options || []).length > 0 ? (
                  <select
                    value={form.sport}
                    onChange={(e) => setForm({ ...form, sport: e.target.value })}
                    required
                    className="w-full h-10 rounded-md border bg-transparent px-3 text-sm"
                    data-testid="public-form-sport"
                  >
                    <option value="">Choose a sport/level…</option>
                    {eventType.sport_options.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                ) : (
                  <Input
                    value={form.sport}
                    onChange={(e) => setForm({ ...form, sport: e.target.value })}
                    required
                    placeholder="e.g., Varsity Boys Basketball"
                    data-testid="public-form-sport"
                  />
                )}
              </div>
              <div>
                <Label>Preferred side</Label>
                <div className="flex gap-2 mt-1">
                  {['home', 'away', 'neutral'].map((s) => (
                    <Button
                      key={s}
                      type="button"
                      size="sm"
                      variant={form.side === s ? 'default' : 'outline'}
                      className={form.side === s ? 'bg-amber-600 hover:bg-amber-700' : ''}
                      onClick={() => setForm({ ...form, side: s })}
                    >
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Anything the scheduler should know (officiating, travel, etc.)"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-amber-600 hover:bg-amber-700"
                disabled={submitting}
                data-testid="public-form-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…
                  </>
                ) : (
                  'Request this slot'
                )}
              </Button>
              <p className="text-xs text-slate-500 text-center">
                You'll get an email confirmation. The scheduler reviews and approves.
              </p>
            </form>
          )}
        </div>
      </div>
    </PageShell>
  );
}

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      <header className="border-b border-slate-800/50 py-4 px-6">
        <div className="max-w-5xl mx-auto flex items-center gap-2">
          <CalendarClock className="h-5 w-5 text-amber-500" />
          <span className="font-bold tracking-wide">OmniLeagues</span>
          <span className="text-xs text-slate-500 ml-2">Book a Game</span>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 py-8">{children}</main>
      <footer className="max-w-5xl mx-auto px-4 py-6 text-center text-xs text-slate-500">
        Powered by OmniLeagues · secure booking
      </footer>
    </div>
  );
}
